import { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Plus, Trash, PencilSimple, ArrowUp, ArrowDown, Eye, EyeSlash, Columns as ColumnsIcon, WarningCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { Navigate } from 'react-router-dom';

// Static "system" columns — protected; admin can only toggle visibility/order via user preferences,
// not edit their type or key. Shown for context so admin sees the complete column landscape.
const SYSTEM_COLUMNS = [
  { key: 'payment_no', label: 'رقم الدفعة', type: 'text' },
  { key: 'branch', label: 'الفرع', type: 'text' },
  { key: 'contract_no', label: 'رقم العقد', type: 'text' },
  { key: 'payment_status', label: 'حالة الدفعة', type: 'select' },
  { key: 'follow_up_status', label: 'حالة المتابعة', type: 'select' },
  { key: 'classification', label: 'تصنيف الفاتورة', type: 'select' },
  { key: 'period_from', label: 'الفترة من', type: 'date' },
  { key: 'period_to', label: 'الفترة إلى', type: 'date' },
  { key: 'invoice_amount', label: 'قيمة الدفعة', type: 'money' },
  { key: 'collected_amount', label: 'المبلغ المحصل', type: 'money' },
  { key: 'remaining_amount', label: 'المبلغ المتبقي', type: 'money' },
  { key: 'assigned_user_name', label: 'الموظف المسؤول', type: 'text' },
  { key: 'due_date', label: 'تاريخ الدفعة', type: 'date' },
  { key: 'issue_date', label: 'تاريخ إصدار الدفعة', type: 'date' },
  { key: 'payment_date', label: 'تاريخ السداد', type: 'date' },
  { key: 'posting_month', label: 'شهر الترحيل', type: 'text' },
  { key: 'asset_system_no', label: 'رقم نظام الأصول', type: 'text' },
  { key: 'asset_no', label: 'رقم الأصل', type: 'text' },
  { key: 'contract_start', label: 'تاريخ بداية العقد', type: 'date' },
  { key: 'contract_end', label: 'تاريخ نهاية العقد', type: 'date' },
  { key: 'contract_type', label: 'نوع العقد', type: 'text' },
  { key: 'invoice_no', label: 'رقم الفاتورة', type: 'text' },
  { key: 'first_imported_at', label: 'تاريخ الاستيراد', type: 'datetime' },
  { key: 'last_yardi_update_at', label: 'تاريخ التحديث', type: 'datetime' },
];

const TYPE_LABELS = {
  text: 'نص', number: 'رقم', money: 'مبلغ', date: 'تاريخ', datetime: 'تاريخ ووقت',
  select: 'قائمة منسدلة', boolean: 'نعم/لا', email: 'بريد إلكتروني', phone: 'رقم هاتف',
};

export default function ColumnsAdmin() {
  const { user } = useAuth();
  const [cols, setCols] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { key: '', label: '', type: 'text', required: false, hidden: false, options: [], allowed_roles: [] };
  const [form, setForm] = useState(empty);
  const [optInput, setOptInput] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/custom-columns');
      setCols(data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); api.get('/roles').then(({ data }) => setRoles(data)).catch(() => {}); }, [load]);

  if (user && user.role !== 'admin') return <Navigate to="/" replace />;

  const openCreate = () => { setEditing(null); setForm(empty); setOptInput(''); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      key: c.key, label: c.label, type: c.type,
      required: !!c.required, hidden: !!c.hidden,
      options: c.options || [], allowed_roles: c.allowed_roles || [],
    });
    setOptInput('');
    setOpen(true);
  };
  const save = async () => {
    if (!form.label?.trim()) { toast.error('اسم العمود مطلوب'); return; }
    try {
      if (editing) {
        await api.put(`/custom-columns/${editing.id}`, {
          label: form.label.trim(),
          type: form.type,
          required: form.required,
          hidden: form.hidden,
          options: form.type === 'select' ? form.options : [],
          allowed_roles: form.allowed_roles,
        });
        toast.success('تم تحديث العمود');
      } else {
        if (!form.key?.trim()) { toast.error('المفتاح (المعرّف الإنجليزي) مطلوب'); return; }
        await api.post('/custom-columns', {
          key: form.key.trim(),
          label: form.label.trim(),
          type: form.type,
          required: form.required,
          hidden: form.hidden,
          options: form.type === 'select' ? form.options : [],
          allowed_roles: form.allowed_roles,
        });
        toast.success('تم إنشاء العمود بنجاح');
      }
      setOpen(false); setEditing(null); load();
    } catch (err) { toast.error('فشل: ' + (err.response?.data?.detail || err.message)); }
  };
  const del = async (c) => {
    if (!window.confirm(`حذف العمود "${c.label}"؟\nسيتم مسح قيم هذا العمود من جميع الفواتير.`)) return;
    try { await api.delete(`/custom-columns/${c.id}`); toast.success('تم الحذف'); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'فشل الحذف'); }
  };
  const move = async (c, dir) => {
    const idx = cols.findIndex(x => x.id === c.id);
    const target = dir === 'up' ? cols[idx - 1] : cols[idx + 1];
    if (!target) return;
    try {
      await Promise.all([
        api.put(`/custom-columns/${c.id}`, { order: target.order }),
        api.put(`/custom-columns/${target.id}`, { order: c.order }),
      ]);
      load();
    } catch { toast.error('فشل تحريك العمود'); }
  };
  const toggleHidden = async (c) => {
    try { await api.put(`/custom-columns/${c.id}`, { hidden: !c.hidden }); load(); }
    catch { toast.error('فشل'); }
  };
  const addOption = () => {
    const v = optInput.trim();
    if (!v) return;
    if (form.options.includes(v)) { toast.error('الخيار موجود'); return; }
    setForm({ ...form, options: [...form.options, v] });
    setOptInput('');
  };
  const removeOption = (o) => setForm({ ...form, options: form.options.filter(x => x !== o) });
  const toggleRole = (code) => {
    const s = new Set(form.allowed_roles);
    if (s.has(code)) s.delete(code); else s.add(code);
    setForm({ ...form, allowed_roles: Array.from(s) });
  };

  return (
    <div className="space-y-6" data-testid="columns-admin-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-[#B4935A] mb-1">Column Manager</div>
        <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3"><ColumnsIcon size={30} className="text-[#B4935A]" /> إدارة الأعمدة</h1>
        <p className="text-sm text-slate-500 mt-1">أضف أعمدة مخصّصة للفواتير دون تعديل الكود. الأعمدة الجديدة قابلة للبحث والتصفية والفرز فوراً.</p>
      </div>

      {/* Custom columns */}
      <Card className="border border-slate-200 rounded-md shadow-none p-0 overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <div>
            <div className="text-sm font-semibold text-slate-900">الأعمدة المخصّصة</div>
            <div className="text-xs text-slate-500">
              {cols.length} عمود مخصّص · تخزينها تحت <code className="mono bg-slate-100 px-1 rounded">custom_data</code> على كل فاتورة
            </div>
          </div>
          <Button onClick={openCreate} className="bg-slate-900 text-white" data-testid="new-column-btn"><Plus className="ms-1" size={16} /> عمود جديد</Button>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-600">
            <tr>
              <th className="text-start p-3 w-14">#</th>
              <th className="text-start p-3">الاسم</th>
              <th className="text-start p-3">المفتاح</th>
              <th className="text-start p-3">النوع</th>
              <th className="text-start p-3">إلزامي</th>
              <th className="text-start p-3">صلاحيات</th>
              <th className="text-start p-3">الحالة</th>
              <th className="text-start p-3">الإجراءات</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (<tr><td colSpan={8} className="p-6 text-center text-slate-400">جاري التحميل...</td></tr>)
              : cols.length === 0 ? (
                <tr><td colSpan={8} className="p-8 text-center text-slate-400">لا توجد أعمدة مخصّصة. اضغط "عمود جديد" للبدء.</td></tr>
              ) : cols.map((c, idx) => (
                <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`col-row-${c.key}`}>
                  <td className="p-3 text-xs text-slate-500">{c.order}</td>
                  <td className="p-3 font-medium">{c.label}</td>
                  <td className="p-3 mono text-xs text-slate-600">custom.{c.key}</td>
                  <td className="p-3"><Badge variant="outline">{TYPE_LABELS[c.type] || c.type}</Badge></td>
                  <td className="p-3">{c.required ? <Badge className="bg-red-100 text-red-800 hover:bg-red-100">إلزامي</Badge> : <span className="text-slate-400 text-xs">اختياري</span>}</td>
                  <td className="p-3 text-xs">
                    {(c.allowed_roles || []).length === 0 ? <span className="text-slate-400">الكل</span>
                     : (c.allowed_roles || []).map(r => <span key={r} className="mr-1 bg-slate-100 px-2 py-0.5 rounded text-[10px]">{r}</span>)}
                  </td>
                  <td className="p-3">
                    {c.hidden
                      ? <span className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-500 px-2 py-1 rounded"><EyeSlash size={12} /> مخفي</span>
                      : <span className="inline-flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 px-2 py-1 rounded"><Eye size={12} /> ظاهر</span>}
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" title="أعلى" disabled={idx === 0} onClick={() => move(c, 'up')}><ArrowUp size={14} /></Button>
                      <Button size="icon" variant="ghost" title="أسفل" disabled={idx === cols.length - 1} onClick={() => move(c, 'down')}><ArrowDown size={14} /></Button>
                      <Button size="icon" variant="ghost" title={c.hidden ? 'إظهار' : 'إخفاء'} onClick={() => toggleHidden(c)}>{c.hidden ? <Eye size={14} /> : <EyeSlash size={14} />}</Button>
                      <Button size="icon" variant="ghost" title="تعديل" onClick={() => openEdit(c)} data-testid={`edit-col-${c.key}`}><PencilSimple size={14} /></Button>
                      <Button size="icon" variant="ghost" title="حذف" onClick={() => del(c)} data-testid={`del-col-${c.key}`}><Trash size={14} className="text-red-600" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </Card>

      {/* System columns — read-only reference */}
      <Card className="border border-slate-200 rounded-md shadow-none p-0 overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <div>
            <div className="text-sm font-semibold text-slate-900">الأعمدة الأساسية <span className="text-xs text-slate-500">(محميّة — للعرض فقط)</span></div>
            <div className="text-xs text-slate-500 flex items-center gap-1 mt-1"><WarningCircle size={12} /> يمكن إخفاء أو تعديل ترتيب هذه الأعمدة من داخل جدول الفواتير مباشرة (زر "أعمدة")</div>
          </div>
        </div>
        <div className="p-3 grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
          {SYSTEM_COLUMNS.map(c => (
            <div key={c.key} className="flex items-center justify-between border border-slate-100 rounded px-3 py-2">
              <span>{c.label}</span>
              <span className="text-[10px] mono text-slate-400">{c.key} · {TYPE_LABELS[c.type]}</span>
            </div>
          ))}
        </div>
      </Card>

      {/* Create/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir="rtl" className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? `تعديل العمود — ${editing.label}` : 'عمود مخصّص جديد'}</DialogTitle>
            <DialogDescription>سيصبح العمود قابلاً للبحث والتصفية والفرز تلقائياً في جدول الفواتير.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1 block">المفتاح (إنجليزي، بدون فراغات) *</Label>
                <Input value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} disabled={!!editing} placeholder="priority_level" className="mono" data-testid="col-key" />
                <div className="text-[11px] text-slate-500 mt-1">سيُخزَّن كـ <code className="mono">custom_data.{form.key || '...'}</code></div>
              </div>
              <div>
                <Label className="mb-1 block">اسم العرض (عربي) *</Label>
                <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="مستوى الأولوية" data-testid="col-label" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1 block">نوع البيانات</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="col-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPE_LABELS).map(([code, label]) => <SelectItem key={code} value={code}>{label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end gap-6">
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <Checkbox checked={form.required} onCheckedChange={(v) => setForm({ ...form, required: !!v })} data-testid="col-required" /> إلزامي
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <Checkbox checked={form.hidden} onCheckedChange={(v) => setForm({ ...form, hidden: !!v })} data-testid="col-hidden" /> مخفي افتراضياً
                </label>
              </div>
            </div>

            {form.type === 'select' && (
              <div>
                <Label className="mb-1 block">خيارات القائمة المنسدلة</Label>
                <div className="flex gap-2">
                  <Input value={optInput} onChange={(e) => setOptInput(e.target.value)} placeholder="أضف خياراً واضغط Enter" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addOption(); } }} data-testid="col-option-input" />
                  <Button onClick={addOption} variant="outline" data-testid="add-option">إضافة</Button>
                </div>
                {form.options.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {form.options.map(o => (
                      <span key={o} className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 px-2 py-0.5 rounded text-xs">
                        {o}
                        <button type="button" onClick={() => removeOption(o)} className="text-red-600 hover:text-red-800">×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div>
              <Label className="mb-2 block">الصلاحيات <span className="text-xs text-slate-500">(اترك فارغاً لجعل العمود متاحاً لجميع الأدوار)</span></Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 border border-slate-200 rounded p-3 max-h-40 overflow-y-auto">
                {roles.map(r => (
                  <label key={r.code} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={form.allowed_roles.includes(r.code)} onCheckedChange={() => toggleRole(r.code)} />
                    {r.name}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>إلغاء</Button>
            <Button onClick={save} className="bg-slate-900 text-white" data-testid="save-col-btn">{editing ? 'حفظ التعديلات' : 'إنشاء العمود'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
